import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GAP = 4;
const MARGEN = 8;
const MIN_ALTO = 80;
const MAX_ALTO = 260;

// Dropdown accesible (combobox + listbox) para reemplazar un <select> nativo
// cuando hace falta controlar el posicionamiento del listado — ver Login.jsx,
// campo "Profesion": el listado de un <select> nativo lo posiciona el motor
// del navegador/SO y no se puede anclar/clampear al viewport en mobile.
//
// Portal a <body> + position:fixed, mismo motivo que ModalExportarPDF/
// ModalConfirmacion (utils/useAnchoredModalStyle.js): un transform/filter en
// un ancestro rompería el cálculo de posición contra el viewport.
function SelectPersonalizado({
  id, ariaLabelledBy, options, value, onChange, placeholder = 'Seleccionar...', className = '',
}) {
  const opciones = [{ value: '', label: placeholder }, ...options];
  const indiceSeleccionado = Math.max(0, opciones.findIndex(o => o.value === value));

  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(indiceSeleccionado);
  const [estilo, setEstilo] = useState(null);

  const controlRef = useRef(null);
  const listboxRef = useRef(null);
  const opcionRefs = useRef([]);
  const idGenerado = useId();
  const listboxId = `${id || idGenerado}-listbox`;

  const calcularPosicion = () => {
    const control = controlRef.current;
    if (!control) return;
    const rect = control.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const espacioAbajo = vh - rect.bottom - MARGEN;
    const espacioArriba = rect.top - MARGEN;
    const base = { position: 'fixed', left: rect.left, width: rect.width, maxWidth: vw - 2 * MARGEN };

    if (espacioAbajo >= 150 || espacioAbajo >= espacioArriba) {
      setEstilo({ ...base, top: rect.bottom + GAP, maxHeight: Math.min(MAX_ALTO, Math.max(espacioAbajo - GAP, MIN_ALTO)) });
    } else {
      setEstilo({ ...base, bottom: vh - rect.top + GAP, maxHeight: Math.min(MAX_ALTO, Math.max(espacioArriba - GAP, MIN_ALTO)) });
    }
  };

  // Recalcula si el viewport cambia con el listado abierto (rotación, DevTools responsive).
  useEffect(() => {
    if (!abierto) return;
    calcularPosicion();
    const onResize = () => calcularPosicion();
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onClickFuera = (e) => {
      if (controlRef.current?.contains(e.target)) return;
      if (listboxRef.current?.contains(e.target)) return;
      setAbierto(false);
    };
    document.addEventListener('mousedown', onClickFuera);
    return () => document.removeEventListener('mousedown', onClickFuera);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    setResaltado(indiceSeleccionado);
    opcionRefs.current[indiceSeleccionado]?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const abrir = () => setAbierto(true);
  const cerrar = () => { setAbierto(false); controlRef.current?.focus(); };

  const seleccionar = (index) => {
    onChange(opciones[index].value);
    cerrar();
  };

  const moverResaltado = (delta) => {
    setResaltado(prev => {
      const next = Math.min(Math.max(prev + delta, 0), opciones.length - 1);
      opcionRefs.current[next]?.scrollIntoView({ block: 'nearest' });
      return next;
    });
  };

  const onKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        abierto ? moverResaltado(1) : abrir();
        break;
      case 'ArrowUp':
        e.preventDefault();
        abierto ? moverResaltado(-1) : abrir();
        break;
      case 'Home':
        if (abierto) { e.preventDefault(); setResaltado(0); opcionRefs.current[0]?.scrollIntoView({ block: 'nearest' }); }
        break;
      case 'End': {
        if (!abierto) break;
        e.preventDefault();
        const ultimo = opciones.length - 1;
        setResaltado(ultimo);
        opcionRefs.current[ultimo]?.scrollIntoView({ block: 'nearest' });
        break;
      }
      case 'Enter':
      case ' ':
        e.preventDefault();
        abierto ? seleccionar(resaltado) : abrir();
        break;
      case 'Escape':
        if (abierto) { e.preventDefault(); cerrar(); }
        break;
      case 'Tab':
        if (abierto) setAbierto(false);
        break;
      default:
        break;
    }
  };

  const seleccionada = opciones[indiceSeleccionado];

  return (
    <>
      <div
        id={id}
        ref={controlRef}
        role="combobox"
        tabIndex={0}
        aria-expanded={abierto}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-labelledby={ariaLabelledBy}
        aria-activedescendant={abierto ? `${listboxId}-opt-${resaltado}` : undefined}
        className={`login-input select-personalizado-control ${className}`.trim()}
        onClick={() => (abierto ? setAbierto(false) : abrir())}
        onKeyDown={onKeyDown}
      >
        <span className={seleccionada.value === '' ? 'select-personalizado-placeholder' : ''}>
          {seleccionada.label}
        </span>
        <span className={`select-personalizado-flecha${abierto ? ' select-personalizado-flecha-abierta' : ''}`} aria-hidden="true">▾</span>
      </div>

      {abierto && estilo && createPortal(
        <ul
          id={listboxId}
          ref={listboxRef}
          role="listbox"
          aria-labelledby={ariaLabelledBy}
          className="select-personalizado-listbox"
          style={estilo}
        >
          {opciones.map((op, index) => (
            <li
              key={op.value || '__placeholder__'}
              id={`${listboxId}-opt-${index}`}
              ref={(el) => { opcionRefs.current[index] = el; }}
              role="option"
              aria-selected={index === indiceSeleccionado}
              className={`select-personalizado-opcion${index === resaltado ? ' select-personalizado-opcion-resaltada' : ''}${index === indiceSeleccionado ? ' select-personalizado-opcion-seleccionada' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => seleccionar(index)}
              onMouseEnter={() => setResaltado(index)}
            >
              {op.label}
            </li>
          ))}
        </ul>,
        document.body
      )}
    </>
  );
}

export default SelectPersonalizado;
